import { Component, OnDestroy, OnInit } from '@angular/core';
import { SelectOption } from '@bcl/design-system';
import { BehaviorSubject, ReplaySubject, takeUntil } from 'rxjs';
import { IPage } from '@bcl/common-ui';
import { Router } from '@angular/router';
import { IChatMessageWithTimestamp, ITextChatMessage } from '@bcl/chat';
import { IFileChatMessage } from '@bcl/chat/lib/file-messages';
import { ChatListService } from './chat-list.service';
import { environment } from 'src/environments';
import { VisitDayDetailsService } from 'src/app/pages/visit-day-details/visit-day-details.service';

export type ChatsListPage = {
  page: IPage<ChatsListItem>;
};

export type ChatsListItem = {
  visitId: number;
  unreadMessagesCount: number;
  lastMessage: IChatMessageWithTimestamp & {
    userFullName?: string;
  };
};

const unreadStatus = 'Unread';

const componentSelector = 'pc-visit-chat-list';

@Component({
  selector: componentSelector,
  templateUrl: './chat-list.component.html',
  styleUrls: ['./chat-list.component.scss'],
  providers: [ChatListService],
})
export class ChatListComponent implements OnInit, OnDestroy {
  public readonly statusOptions: SelectOption[] = [
    { label: 'CHATS_LIST.STATUS.UNREAD', value: unreadStatus },
  ];
  public readonly chats$ = new BehaviorSubject<ChatsListItem[]>([]);
  public hasUnreadChats = false;
  public readonly globalDateFormatWithTime =
    environment.globalDateFormatWithTime;
  public isLoadingPage = false;
  public totalChats = 0;
  public pageSize = 10;
  public currentPage = 1;
  public showItemList = true;

  private lastPage = 0;
  private readonly destroyed$ = new ReplaySubject<void>(1);

  constructor(
    private chatsListService: ChatListService,
    private router: Router,
    private visitDayDetailsService: VisitDayDetailsService
  ) {}

  public get hasNextPage(): boolean {
    return this.currentPage < this.lastPage;
  }

  public readonly textMessageGuard = (_: unknown): _ is ITextChatMessage =>
    true;
  public readonly fileMessageGuard = (_: unknown): _ is IFileChatMessage =>
    true;

  public ngOnInit(): void {
    this.loadInitialChats();
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public showItems(): void {
    this.showItemList = !this.showItemList;
  }

  public loadInitialChats(): void {
    this.loadChats(1);

    this.chatsListService
      .hasUnreadChats()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((hasUnreadChats) => (this.hasUnreadChats = hasUnreadChats));
  }

  public reloadChats(): void {
    this.currentPage = 1;
    this.chats$.next([]);
    this.loadInitialChats();
  }

  public loadMoreChats(): void {
    this.currentPage++;
    this.loadChats(this.currentPage);
  }

  public navigateToVisitChat(visitId: number): void {
    this.visitDayDetailsService
      .getOrCreateVisitChat(visitId.toString())
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (chatData) => {
          if (chatData?.chatId) {
            this.router.navigate(['/chat', chatData.chatId], {
              state: {
                visitId,
                backUrl: '/chats',
              },
            });
          }
        },
        error: (error) => {
          console.error('Error getting or creating chat:', error);
        },
      });
  }

  private loadChats(pageNumber: number): void {
    this.isLoadingPage = true;
    this.chatsListService
      .getChatsPage({ pageNumber, pageSize: this.pageSize })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (result) => {
          this.isLoadingPage = false;
          const newChats = [
            ...(pageNumber > 1 ? this.chats$.value : []),
            ...result.page.elements,
          ];
          this.chats$.next(newChats);
          this.totalChats = result.page.totalEntries;
          this.lastPage = Math.ceil(result.page.totalEntries / this.pageSize);
        },
        error: (error) => {
          console.error('Error loading chats:', error);
          this.isLoadingPage = false;
        },
      });
  }
}
